import React, { useState, useEffect } from 'react';
import { HiCheckCircle, HiChevronRight, HiOutlineChatBubbleLeftRight, HiArrowUpRight } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import { saveEnquiry } from '../data/enquiriesData';

export default function GeneratorMaintenance() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    organisation: '',
    segment: 'Retail',
    capacity: '',
    serviceNeeded: 'Preventive Maintenance',
    message: ''
  });
  const [activeTab, setActiveTab] = useState('retail');
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceSelect = (service) => {
    setFormData(prev => ({ ...prev, serviceNeeded: service }));
    const formElement = document.getElementById('maintenance-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const nameInput = document.getElementById('client-name');
      if (nameInput) nameInput.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneDigits = formData.phone.trim();
    if (!/^\d{10}$/.test(phoneDigits)) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Please enter a valid 10-digit number.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      Swal.fire({
        title: 'Invalid Email Address',
        text: 'Please enter a valid email address.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    setIsSubmitted(true);

    const newEnquiry = {
      id: `enq-${Date.now()}`,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      city: formData.organisation || '',
      message: `Service needed: ${formData.serviceNeeded} for a ${formData.capacity} generator. Segment: ${formData.segment}. Message: ${formData.message}`,
      type: 'maintenance',
      status: 'pending',
      date: new Date().toISOString().replace('T', ' ').slice(0, 16)
    };

    try {
      await saveEnquiry(newEnquiry, true);
      Swal.fire({
        title: 'Service Request Logged!',
        text: `Thank you, ${formData.name}. We have registered your request for ${formData.serviceNeeded} for a ${formData.capacity} generator. Our support coordinator will get in touch with you shortly.`,
        icon: 'success',
        confirmButtonColor: '#0F5A3A'
      });
      setFormData({
        name: '', phone: '', email: '', organisation: '', segment: 'Retail',
        capacity: '', serviceNeeded: 'Preventive Maintenance', message: ''
      });
      e.target.reset();
    } catch (err) {
      Swal.fire('Error', 'Failed to submit service request. Please try again.', 'error');
    } finally {
      setIsSubmitted(false);
    }
  };

  const services = [
    { name: 'Preventive Maintenance (PM)', desc: 'Scheduled inspections, oil analysis, filter replacements, and key diagnostics based on generator runtime.' },
    { name: 'Breakdown Maintenance', desc: 'Fast, on-call engine/electrical diagnostics and technical troubleshooting to restore standby power.' },
    { name: 'Annual Maintenance Contracts (AMC)', desc: 'Standard contract packages protecting operations through regular servicing visits.' },
    { name: 'Comprehensive AMC (CAMC)', desc: 'Full-risk coverage contract including all wear-and-tear spares, filters, and lubricating oil.' },
    { name: 'Corrective Maintenance', desc: 'Pre-planned restorative maintenance, component rebuilds, and cylinder-head optimizations.' },
    { name: 'Electrical Repair Works', desc: 'Relay programming, automatic mains failure (AMF) panel sync, and cabling fault updates.' },
    { name: 'Mechanical Repairs', desc: 'Top/major overhaul, cooling system cleaning, radiators repair, and exhaust piping layout changes.' },
    { name: 'Spare Parts Supply', desc: '100% genuine OEM spares, hoses, V-belts, starters, charging alternators, and gasket kits.' },
    { name: 'Equipment Handling', desc: 'Professional generator alignment, shifting, rigging, and placement services.' },
    { name: 'Standard Generator Servicing', desc: 'General checking, battery cell electrolyte audits, and general lubricant top-ups.' }
  ];

  const segmentContent = {
    retail: {
      title: 'Retail & Commercial Standby Support',
      desc: 'Configured for hotels, hospitals, schools, petrol pumps, farmhouses, and diaries. We provide pre-scheduled preventive maintenance visits aligned with standard run-hour indicators.',
      points: ['Silent operation audits', 'Quick dispatch for power flickers']
    },
    telecom: {
      title: 'Telecom Infrastructure AMC & CAMC',
      desc: 'High-density SLA-bound support powering telecom towers. Backed by remote monitoring, we deliver structured contracts for major networks (Reliance Jio, ATC, TVIPL, BSNL, GTL Infra) ensuring strict uptime.',
      points: ['Guaranteed < 2-hour response SLA', 'Preventive care for remote locations']
    },
    corporate: {
      title: 'Corporate, Heavy Industrial & Government',
      desc: 'Heavy infrastructure backups, public utility water pumping systems, and railway NOC sites. Our technicians follow high safety and standards compliance.',
      points: ['AMF relay calibration & panel test', 'High kVA industrial load testing']
    }
  };

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/Generator Solutions.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Power Services</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Maintenance & <span className="text-gradient-gold">AMC Services</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Ensure maximum uptime for your generator systems. Backed by GPS-routed maintenance trucks, certified parts, and quick technical response.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <span>Home</span>
          <span className="text-border-dark">/</span>
          <span>Generator Solutions</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Maintenance & AMC</span>
        </div>
      </div>

      {/* Services Grid */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="eyebrow">Our Capabilities</span>
          <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading mb-4">Complete Generator Support</h2>
          <p className="text-base text-body leading-relaxed">
            We deliver end-to-end electrical and mechanical repair services for silent generators of any capacity or brand.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((srv, idx) => (
            <div key={idx} className="bg-white border border-border rounded-2xl p-6 shadow-card hover:shadow-card-hover hover:-translate-y-1 hover:border-primary/20 transition-all duration-300 flex justify-between items-start group">
              <div className="flex items-start gap-4 flex-1 pr-4">
                <div className="w-1 h-full min-h-[40px] bg-gradient-to-b from-accent to-transparent rounded-full flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xs font-bold text-heading uppercase tracking-wide mb-2 group-hover:text-primary transition-colors">{srv.name}</h3>
                  <p className="text-xs text-body leading-relaxed">{srv.desc}</p>
                </div>
              </div>
              <button
                onClick={() => handleServiceSelect(srv.name.split(' (')[0])}
                className="w-8 h-8 rounded-xl bg-light-section text-secondary hover:bg-primary hover:text-white flex items-center justify-center transition-all duration-200 flex-shrink-0 ml-2"
              >
                <HiChevronRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Segment Tabs */}
      <section className="py-20 bg-light-section border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">Tailored SLA Tiers</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Service Support by Sector</h2>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {['retail', 'telecom', 'corporate'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === tab
                    ? 'bg-primary text-white shadow-premium'
                    : 'bg-white border border-border text-body hover:text-primary hover:border-primary/30 shadow-card'
                  }`}
              >
                {tab === 'corporate' ? 'Corporate & Government' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="bg-white border border-border rounded-2xl p-8 shadow-card max-w-4xl mx-auto">
            <h3 className="text-base font-display font-extrabold text-heading uppercase mb-4">{segmentContent[activeTab].title}</h3>
            <p className="text-sm text-body leading-relaxed mb-6">{segmentContent[activeTab].desc}</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {segmentContent[activeTab].points.map((pt, i) => (
                <li key={i} className="flex items-center gap-3 text-sm font-semibold text-heading">
                  <HiCheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  {pt}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Process Journey */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="eyebrow">Our Process</span>
          <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Simplified Service Journey</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {[
            { step: '01', title: 'Submit Enquiry', desc: 'Log request detailing genset brand, capacity, and current symptoms.' },
            { step: '02', title: 'Quotation', desc: 'Receive immediate transparent pricing for parts or service call fees.' },
            { step: '03', title: 'Service Plan', desc: 'Deploy coordinator schedules technician visit to client site.' },
            { step: '04', title: 'Scheduled Visit', desc: 'Certified engine mechanic completes preventative service or breakdown audit.' },
            { step: '05', title: 'Payment & Close', desc: 'Approve digital work order. Confirm backup runs smoothly.' }
          ].map((proc, idx) => (
            <div key={idx} className="bg-white border border-border rounded-2xl p-6 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 relative group">
              <span className="absolute top-4 right-4 text-xl md:text-2xl lg:text-2xl font-extrabold font-display text-accent/12 group-hover:text-accent/20 transition-colors">
                {proc.step}
              </span>
              <h4 className="text-xs font-bold text-heading uppercase tracking-wide mb-2 mt-1">{proc.title}</h4>
              <p className="text-xs text-body leading-relaxed">{proc.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AMC Block + Form */}
      <section className="py-24 bg-light-section border-t border-border flex flex-col items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full flex flex-col items-center">

          {/* AMC Info */}
          <div className="w-full mb-16 flex flex-col items-center text-center">
            <span className="eyebrow">AMC & CAMC Contracts</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading mb-6">Custom Annual Service Contracts</h2>
            <p className="text-sm text-body leading-relaxed mb-8 max-w-2xl">
              Green Gusto contracts take full accountability for your backup infrastructure. We support scheduled preventive care, fluids checks, filter replacements, and include emergency callout support.
            </p>
            <div className="bg-white border border-border rounded-2xl p-6 shadow-card max-w-xl w-full text-left">
              <h4 className="text-sm font-bold text-heading mb-3 text-center">Pricing & Tiers Snapshot</h4>
              <p className="text-xs text-body leading-relaxed mb-5 text-center">
                AMC pricing varies based on generator kVA rating, location distance, and emission requirements (CPCB guidelines).
              </p>
              <div className="flex justify-center">
                <button
                  onClick={() => handleServiceSelect('AMC Enquiry')}
                  className="btn-primary text-xs py-3 px-6 rounded-xl"
                >
                  Contact us for a Custom AMC Plan
                  <HiArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Form Section Title */}
          <div className="text-center max-w-3xl mx-auto mb-10 mt-6">
            <span className="eyebrow">Direct Quotation</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Request a Service Quotation</h2>
            <p className="text-sm text-body-muted mt-2">Submit details for a customized annual maintenance contract (AMC) or repair service call.</p>
          </div>

          {/* Form */}
          <div id="maintenance-form" className="bg-charcoal text-white border border-white/5 rounded-3xl p-8 md:p-10 shadow-premium relative overflow-hidden max-w-5xl mx-auto">
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-accent/8 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-2">
                <HiOutlineChatBubbleLeftRight className="w-5 h-5 text-accent" />
                <span className="text-xs font-bold text-accent uppercase tracking-widest">Log Support Request</span>
              </div>
              <h3 className="text-xl font-display font-extrabold text-white mb-2">Schedule a Service Visit</h3>
              <p className="text-xs text-white/60 mb-8">Log diagnostic details to schedule a technician or request custom AMC pricing.</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Row 1: Name, Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Full Name</label>
                    <input id="client-name" type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter your name" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Contact Number</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="e.g. 9876543210" className="input-dark" />
                  </div>
                </div>

                {/* Row 2: Email, Organisation */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Email Address</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="name@company.com" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Organisation Name</label>
                    <input type="text" name="organisation" value={formData.organisation} onChange={handleChange} required placeholder="Enter company name" className="input-dark" />
                  </div>
                </div>

                {/* Row 3: Segment, Capacity, Service Needed */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Segment</label>
                    <select name="segment" value={formData.segment} onChange={handleChange} required className="input-dark bg-charcoal text-white/85 appearance-none w-full">
                      <option value="Retail" className="bg-white text-heading">Retail</option>
                      <option value="Telecom" className="bg-white text-heading">Telecom</option>
                      <option value="Corporate" className="bg-white text-heading">Corporate</option>
                      <option value="Government" className="bg-white text-heading">Government</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Genset Capacity</label>
                    <input type="text" name="capacity" value={formData.capacity} onChange={handleChange} required placeholder="e.g. 125 kVA" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Service Needed</label>
                    <select name="serviceNeeded" value={formData.serviceNeeded} onChange={handleChange} required className="input-dark bg-charcoal text-white/85 appearance-none w-full">
                      <option value="Preventive Maintenance" className="bg-white text-heading">Preventive Maintenance</option>
                      <option value="Breakdown Repair" className="bg-white text-heading">Breakdown Repair</option>
                      <option value="AMC Enquiry" className="bg-white text-heading">AMC Enquiry</option>
                      <option value="Electrical Repair" className="bg-white text-heading">Electrical Repair</option>
                      <option value="Spares Supply" className="bg-white text-heading">Spares Supply</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                  <div className="md:col-span-8">
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Message Details</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows={2} required placeholder="Tell us symptoms, leakage, noise..." className="input-dark resize-none" />
                  </div>
                  <div className="md:col-span-4">
                    <button type="submit" disabled={isSubmitted} className="btn-green rounded-xl w-full justify-center py-3.5 text-sm disabled:opacity-60">
                      Submit Service Request
                      <HiArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
